import {
    FlightOffer,
    MultiClassFlightOffer,
    CabinPrice,
} from "../types/flight";

/**
 * Generate a unique key for a flight based on its segments
 * This key is used to match the same flight across different cabin classes
 */
export const generateFlightKey = (offer: FlightOffer): string => {
    // Use the first itinerary (outbound) for matching
    const itinerary = offer.itineraries[0];
    if (!itinerary || !itinerary.segments || itinerary.segments.length === 0) {
        return "";
    }

    // Build key from all segments to ensure exact flight match
    const segmentKeys = itinerary.segments
        .map(
            (seg) =>
                `${seg.carrierCode}${seg.number}-${seg.departure.iataCode}${seg.arrival.iataCode}-${seg.departure.at}`
        )
        .join("_");

    return segmentKeys;
};

/**
 * Group flight offers by matching flights across different cabin classes
 */
export const groupFlightsByRoute = (
    economyFlights: FlightOffer[],
    premiumFlights: FlightOffer[],
    businessFlights: FlightOffer[]
): MultiClassFlightOffer[] => {
    const flightMap = new Map<string, MultiClassFlightOffer>();

    // Process economy flights first (as base)
    economyFlights.forEach((flight) => {
        const key = generateFlightKey(flight);
        if (!key) return;

        const firstSegment = flight.itineraries[0].segments[0];

        flightMap.set(key, {
            flightKey: key,
            baseFlightInfo: {
                segments: flight.itineraries[0].segments,
                duration: flight.itineraries[0].duration,
                itineraries: flight.itineraries,
                carrierCode: firstSegment.carrierCode,
                flightNumber: `${firstSegment.carrierCode} ${firstSegment.number}`,
            },
            cabinPrices: {
                economy: {
                    price: flight.price.total,
                    currency: flight.price.currency,
                    offer: flight,
                },
            },
        });
    });

    // Add premium economy prices
    premiumFlights.forEach((flight) => {
        const key = generateFlightKey(flight);
        if (!key) return;

        if (flightMap.has(key)) {
            const existing = flightMap.get(key)!;
            existing.cabinPrices.premiumEconomy = {
                price: flight.price.total,
                currency: flight.price.currency,
                offer: flight,
            };
        } else {
            // Flight only available in premium, not economy
            const firstSegment = flight.itineraries[0].segments[0];

            flightMap.set(key, {
                flightKey: key,
                baseFlightInfo: {
                    segments: flight.itineraries[0].segments,
                    duration: flight.itineraries[0].duration,
                    itineraries: flight.itineraries,
                    carrierCode: firstSegment.carrierCode,
                    flightNumber: `${firstSegment.carrierCode} ${firstSegment.number}`,
                },
                cabinPrices: {
                    premiumEconomy: {
                        price: flight.price.total,
                        currency: flight.price.currency,
                        offer: flight,
                    },
                },
            });
        }
    });

    // Add business class prices
    businessFlights.forEach((flight) => {
        const key = generateFlightKey(flight);
        if (!key) return;

        if (flightMap.has(key)) {
            const existing = flightMap.get(key)!;
            existing.cabinPrices.business = {
                price: flight.price.total,
                currency: flight.price.currency,
                offer: flight,
            };
        } else {
            // Flight only available in business, not economy or premium
            const firstSegment = flight.itineraries[0].segments[0];

            flightMap.set(key, {
                flightKey: key,
                baseFlightInfo: {
                    segments: flight.itineraries[0].segments,
                    duration: flight.itineraries[0].duration,
                    itineraries: flight.itineraries,
                    carrierCode: firstSegment.carrierCode,
                    flightNumber: `${firstSegment.carrierCode} ${firstSegment.number}`,
                },
                cabinPrices: {
                    business: {
                        price: flight.price.total,
                        currency: flight.price.currency,
                        offer: flight,
                    },
                },
            });
        }
    });

    return Array.from(flightMap.values());
};
